import type { ComponentPropsWithoutRef, JSX, ReactNode } from 'react';
import { forwardRef } from 'react';
import { styled } from 'styled-components';
import { isNotNil } from '@wistia/type-guards';
import type { ColorSchemeTypes } from '../ColorSchemeWrapper';
import { getColorScheme } from '../../private/helpers/getColorScheme';

type BadgeVariants = 'default' | 'outline';

type StyledBadgeProps = {
  $colorScheme: ColorSchemeTypes;
  $hasIcon: boolean;
  $variant: BadgeProps['variant'];
};

export type BadgeProps = ComponentPropsWithoutRef<'div'> & {
  /**
   * Sets the [color scheme](/docs/color-schemes)
   */
  colorScheme?: ColorSchemeTypes;
  /**
   * Optional `Icon` component to use. Must be an instance of [Icon](/docs/components/Icon)
   */
  icon?: JSX.Element | undefined;
  /**
   * The text to display in the `Badge`
   */
  label?: ReactNode;
  /**
   * The variant of the `Badge`
   */
  variant?: BadgeVariants;
};

const StyledBadge = styled.div<StyledBadgeProps>`
  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  align-items: center;
  background-color: var(--wui-color-bg-surface-secondary);
  box-shadow: ${({ $variant }) =>
    $variant === 'outline' ? 'inset 0 0 0 1px var(--wui-color-border)' : 'none'};
  border-radius: var(--wui-border-radius-rounded);
  color: var(--wui-color-text);
  display: inline-flex;
  font-size: var(--wui-typography-label-4-size);
  font-weight: var(--wui-typography-label-4-weight);
  gap: var(--wui-space-01);
  justify-content: center;
  line-height: var(--wui-typography-label-3-line-height);
  padding: var(--wui-space-01) var(--wui-space-02);
  width: fit-content;

  /* Icon component size and color */
  && {
    svg {
      color: var(--wui-color-icon);
      height: 12px;
      width: 12px;
      flex: 0 0 12px;
    }
  }
`;

/**
 * A `Badge` is a compact label, with optional icon, to convey status or context.
 */
export const Badge = forwardRef<HTMLDivElement, BadgeProps>(
  ({ colorScheme = 'inherit', label, icon, variant = 'default', ...props }, ref): JSX.Element => {
    const hasIcon = isNotNil(icon);

    return (
      <StyledBadge
        ref={ref}
        {...props}
        $colorScheme={colorScheme}
        $hasIcon={hasIcon}
        $variant={variant}
      >
        {hasIcon ? icon : null}
        {isNotNil(label) ? <span>{label}</span> : null}
      </StyledBadge>
    );
  },
);

Badge.displayName = 'Badge';
